/**
 * Super Admin Dashboard Page
 * 
 * Dashboard exclusivo para Super Admin com visão global de todas as organizações
 * Multi-tenant: Mostra estatísticas consolidadas de todas as organizações
 */

import React, { useEffect, useState } from 'react';
import { Building2, Camera, Users, TrendingUp, AlertCircle, ArrowLeft, Eye } from 'lucide-react';
import { APIConfig } from '../data/config/APIConfig';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/common/Toast';
import { useConfirmModal } from '../components/ConfirmModal';

interface OrganizationStats {
  organization_id: string;
  organization_name: string;
  organization_type?: string;
  total_cameras: number;
  active_cameras: number;
  max_cameras: number;
  total_people_today: number;
  total_vehicles_today?: number;
  total_detections_today: number;
  parking_entries_today?: number;
  parking_exits_today?: number;
  parking_active_sessions?: number;
  status: 'online' | 'offline' | 'warning' | 'no_cameras' | 'error';
  usage_rate: number;
  last_detection: string | null;
  person_tracking_enabled?: boolean;
  suspect_alerts_enabled?: boolean;
  is_trial?: boolean;
  trial_ends_at?: string | null;
  trial_days?: number | null;
}

interface GlobalStats {
  total_organizations: number;
  active_organizations: number;
  total_cameras: number;
  active_cameras: number;
  total_people_today: number;
  total_detections_today: number;
  average_usage_rate: number;
}

interface OrganizationDetails {
  organization: {
    id: string;
    name: string;
    slug: string;
    contact_name: string;
    contact_email: string;
    city: string;
    state: string;
    max_cameras: number;
    total_cameras: number;
    total_locations: number;
    is_active: boolean;
    created_at: string | null;
    organization_type?: string;
  };
  locations: Array<{
    location_id: string;
    location_name: string;
    floor: string | null;
    zone: string | null;
    total_cameras: number;
    total_people_today: number;
    total_detections: number;
    parking_entries?: number;
    parking_exits?: number;
  }>;
  top_cameras: Array<{
    camera_id: string;
    camera_name: string;
    location_name: string;
    people_count: number;
    detections_count: number;
    parking_entries?: number;
    parking_exits?: number;
  }>;
  parking_stats?: {
    entries_today: number;
    exits_today: number;
    employees_today: number;
    visitors_today: number;
    total_sessions: number;
    active_sessions: number;
    avg_dwell_minutes: number;
  };
  generated_at: string;
}

export default function SuperAdminDashboardPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { showConfirm, ModalComponent } = useConfirmModal();
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estado para drill-down
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);
  const [orgDetails, setOrgDetails] = useState<OrganizationDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000); // Atualiza a cada 30s
    return () => clearInterval(interval);
  }, []);

  const loadDashboard = async () => {
    try {
      setError(null);
      const api = APIConfig.getInstance();
      const response = await api.get('/api/v1/stats/superadmin/organizations-overview');
      setGlobalStats(response.data.global_stats);
      setOrganizations(response.data.organizations);
    } catch (err: any) {
      console.error('Erro ao carregar dashboard:', err);
      if (err.response?.status === 403) {
        setError('Acesso negado. Apenas Super Admin pode acessar este dashboard.');
      } else {
        setError('Erro ao carregar dados do dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationDetails = async (orgId: string) => {
    setLoadingDetails(true);
    try {
      const api = APIConfig.getInstance();
      const response = await api.get(`/api/v1/stats/superadmin/organization-details/${orgId}`);
      setOrgDetails(response.data);
      setSelectedOrg(orgId);
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      showToast('Erro ao carregar detalhes da organização', 'error');
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleSuspectAlerts = async (orgId: string, currentStatus: boolean) => {
    const orgName = organizations.find(o => o.organization_id === orgId)?.organization_name || 'organização';
    const action = currentStatus ? 'desativar' : 'ativar';
    
    showConfirm({
      title: `${action === 'ativar' ? 'Ativar' : 'Desativar'} Suspect Alerts`,
      message: `Deseja realmente ${action} Suspect Alerts para ${orgName}?\n\n${currentStatus ? '❌ A organização perderá acesso aos alertas de suspeitos em tempo real' : '✅ A organização terá acesso aos alertas de suspeitos em tempo real (+R$ 300/mês)'}`,
      confirmText: action === 'ativar' ? 'Ativar' : 'Desativar',
      type: currentStatus ? 'warning' : 'success',
      icon: 'alert',
      onConfirm: async () => {
        try {
          const api = APIConfig.getInstance();
          await api.patch(`/api/v1/organizations/${orgId}`, {
            suspect_alerts_enabled: !currentStatus
          });
          
          // Atualizar localmente
          setOrganizations(orgs => 
            orgs.map(org => 
              org.organization_id === orgId 
                ? { ...org, suspect_alerts_enabled: !currentStatus }
                : org
            )
          );
          
          showToast(`Suspect Alerts ${action === 'ativar' ? 'ativado' : 'desativado'} com sucesso!`, 'success');
        } catch (err: any) {
          console.error('Erro ao atualizar feature:', err);
          showToast(`Erro ao ${action} Suspect Alerts: ${err.response?.data?.detail || 'Erro desconhecido'}`, 'error');
        }
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'offline': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'no_cameras': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'error': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return '🟢';
      case 'warning': return '🟡';
      case 'offline': return '🔴';
      case 'no_cameras': return '⚪';
      case 'error': return '🟣';
      default: return '⚪';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'warning': return 'Alerta';
      case 'offline': return 'Offline';
      case 'no_cameras': return 'Sem Câmeras';
      case 'error': return 'Erro';
      default: return 'Desconhecido';
    }
  };

  const isVehicleOrg = (org: OrganizationStats) =>
    org.organization_type === 'vehicle_counting';

  const isParkingOrg = (org: OrganizationStats) =>
    org.organization_type === 'parking';

  const getMainMetric = (org: OrganizationStats) => {
    if (isParkingOrg(org)) return org.parking_entries_today ?? org.total_detections_today;
    if (isVehicleOrg(org)) return (org.total_vehicles_today ?? org.total_detections_today);
    return org.total_people_today;
  };

  const getMainMetricLabel = (org: OrganizationStats) => {
    if (isParkingOrg(org)) return 'Entradas';
    if (isVehicleOrg(org)) return 'Veículos';
    return 'Pessoas';
  };

  const getOrgTypeBadge = (org: OrganizationStats) => {
    if (isParkingOrg(org))
      return <span className="text-xs bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 px-1.5 py-0.5 rounded">🅿️ Estacionamento</span>;
    if (isVehicleOrg(org))
      return <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 px-1.5 py-0.5 rounded">🚗 Veículos</span>;
    return <span className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 px-1.5 py-0.5 rounded">👤 Pessoas</span>;
  };

  const getTrialBadge = (org: OrganizationStats) => {
    if (!org.is_trial) return null;
    const days = org.trial_ends_at
      ? Math.max(0, Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / 86400000))
      : org.trial_days ?? null;
    if (days !== null && days <= 3)
      return <span className="text-xs bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 px-1.5 py-0.5 rounded font-semibold">🧪 Trial {days}d ⚠️</span>;
    return <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 px-1.5 py-0.5 rounded">🧪 Trial{days !== null ? ` ${days}d` : ''}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
            Acesso Negado
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 text-center mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full px-4 py-2.5 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Se está visualizando detalhes de uma organização
  if (selectedOrg && orgDetails) {
    return (
      <div className="p-4 sm:p-6">
        {/* Header Detalhes */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => {
              setSelectedOrg(null);
              setOrgDetails(null);
            }}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Visão Global
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            🏢 {orgDetails.organization.name}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {orgDetails.organization.city}, {orgDetails.organization.state} • 
            {orgDetails.organization.total_cameras} câmeras de {orgDetails.organization.max_cameras} contratadas
          </p>
        </div>

        {/* Cards de Informação */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Localizações</p>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {orgDetails.organization.total_locations}
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Câmeras</p>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {orgDetails.organization.total_cameras}
            </p>
          </div>

          {orgDetails.organization.organization_type === 'parking' && orgDetails.parking_stats ? (
            <>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Entradas / Saídas (Hoje)</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {orgDetails.parking_stats.entries_today.toLocaleString('pt-BR')}
                  <span className="text-lg text-gray-400 mx-1">/</span>
                  <span className="text-2xl text-red-500 dark:text-red-400">
                    {orgDetails.parking_stats.exits_today.toLocaleString('pt-BR')}
                  </span>
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Sessões Ativas Agora</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {orgDetails.parking_stats.active_sessions.toLocaleString('pt-BR')}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {orgDetails.parking_stats.total_sessions} total hoje
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pessoas (Hoje)</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                  {orgDetails.top_cameras.reduce((sum, cam) => sum + cam.people_count, 0).toLocaleString('pt-BR')}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Detecções (Hoje)</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {orgDetails.top_cameras.reduce((sum, cam) => sum + cam.detections_count, 0).toLocaleString('pt-BR')}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Cards extras para parking */}
        {orgDetails.organization.organization_type === 'parking' && orgDetails.parking_stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Funcionários</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {orgDetails.parking_stats.employees_today}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Visitantes</p>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {orgDetails.parking_stats.visitors_today}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Permanência Média</p>
              <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                {orgDetails.parking_stats.avg_dwell_minutes > 0
                  ? `${Math.round(orgDetails.parking_stats.avg_dwell_minutes)}min`
                  : '—'}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Veículos Dentro</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {Math.max(0, orgDetails.parking_stats.entries_today - orgDetails.parking_stats.exits_today)}
              </p>
            </div>
          </div>
        )}

        {/* Localizações */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-8">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              📍 Estatísticas por Localização
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Localização
                  </th>
                  <th className="hidden sm:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Andar/Zona
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Câmeras
                  </th>
                  <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    {orgDetails.organization.organization_type === 'parking' ? 'Entradas' : 'Pessoas (Hoje)'}
                  </th>
                  <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    {orgDetails.organization.organization_type === 'parking' ? 'Saídas' : 'Detecções'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {orgDetails.locations.map((location) => (
                  <tr key={location.location_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {location.location_name}
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {location.floor || location.zone || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm font-semibold text-purple-600 dark:text-purple-400">
                        {location.total_cameras}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {orgDetails.organization.organization_type === 'parking'
                          ? (location.parking_entries ?? location.total_people_today).toLocaleString('pt-BR')
                          : location.total_people_today.toLocaleString('pt-BR')}
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4">
                      <div className="text-sm text-gray-900 dark:text-gray-300">
                        {orgDetails.organization.organization_type === 'parking'
                          ? (location.parking_exits ?? location.total_detections).toLocaleString('pt-BR')
                          : location.total_detections}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Câmeras */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              🏆 Top 5 Câmeras Mais Ativas (Hoje)
            </h2>
          </div>
          
          <div className="p-6">
            <div className="space-y-3">
              {orgDetails.top_cameras.map((camera, index) => (
                <div key={camera.camera_id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-4">
                      {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{camera.camera_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {camera.location_name} •{' '}
                        {orgDetails.organization.organization_type === 'parking'
                          ? `${camera.parking_entries ?? camera.people_count} entradas / ${camera.parking_exits ?? 0} saídas`
                          : `${camera.detections_count} detecções`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {camera.people_count.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {orgDetails.organization.organization_type === 'parking' ? 'entradas' : 'pessoas'}
                    </p>
                  </div>
                </div>
              ))}
              
              {orgDetails.top_cameras.length === 0 && (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  Nenhuma câmera com atividade hoje
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Visão Global (padrão)
  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          🌍 Dashboard Super Admin
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Visão global de todas as organizações</p>
      </div>

      {/* Cards Globais */}
      {globalStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Organizações"
            value={`${globalStats.active_organizations}/${globalStats.total_organizations}`}
            icon={<Building2 className="w-6 h-6" />}
            color="blue"
            subtitle="ativas"
          />
          
          <StatCard
            title="Total Câmeras"
            value={`${globalStats.active_cameras}/${globalStats.total_cameras}`}
            icon={<Camera className="w-6 h-6" />}
            color="purple"
            subtitle="em operação"
          />
          
          <StatCard
            title="Pessoas (Hoje)"
            value={globalStats.total_people_today.toLocaleString('pt-BR')}
            icon={<Users className="w-6 h-6" />}
            color="green"
            subtitle={`${globalStats.total_detections_today.toLocaleString('pt-BR')} detecções`}
          />
          
          <StatCard
            title="Taxa Utilização"
            value={`${globalStats.average_usage_rate.toFixed(1)}%`}
            icon={<TrendingUp className="w-6 h-6" />}
            color="orange"
            subtitle="média das orgs"
          />
        </div>
      )}

      {/* Tabela de Organizações */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden mb-8">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Organizações Ativas
          </h2>
        </div>

        {/* CARDS - Mobile (< md) */}
        <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
          {organizations.map((org) => (
            <div key={org.organization_id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0 mr-2">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {org.organization_name}
                  </p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1">
                    {getOrgTypeBadge(org)}
                    {getTrialBadge(org)}
                    <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(org.status)}`}>
                      {getStatusIcon(org.status)} {getStatusLabel(org.status)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => loadOrganizationDetails(org.organization_id)}
                  disabled={loadingDetails && selectedOrg === org.organization_id}
                  className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition"
                >
                  {loadingDetails && selectedOrg === org.organization_id ? '...' : 'Detalhes'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Câmeras</p>
                  <p className="text-sm font-bold text-purple-600 dark:text-purple-400">
                    {org.active_cameras}/{org.total_cameras}
                  </p>
                  <p className="text-xs text-gray-400">de {org.max_cameras}</p>
                </div>
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">{getMainMetricLabel(org)}</p>
                  <p className="text-sm font-bold text-blue-600 dark:text-blue-400">
                    {getMainMetric(org).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-400">hoje</p>
                </div>
                <div className="text-center bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400">Utilização</p>
                  <p className="text-sm font-bold text-orange-600 dark:text-orange-400">
                    {org.usage_rate.toFixed(0)}%
                  </p>
                  <p className="text-xs text-gray-400">{org.active_cameras}/{org.max_cameras} cams</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Suspect Alerts:
                </span>
                <button
                  onClick={() => toggleSuspectAlerts(org.organization_id, org.suspect_alerts_enabled || false)}
                  className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${
                    org.suspect_alerts_enabled
                      ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400'
                  }`}
                >
                  {org.suspect_alerts_enabled ? '✅ Ativo' : '❌ Inativo'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* TABELA - Desktop (≥ md) */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Organização
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Tipo
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Câmeras
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Hoje
                </th>
                <th className="hidden xl:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Detecções
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Utilização
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Status
                </th>
                <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Suspect Alerts
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {organizations.map((org) => (
                <tr key={org.organization_id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="hidden sm:block w-5 h-5 text-gray-400 dark:text-gray-500 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {org.organization_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          ID: {org.organization_id.slice(0, 8)}...
                        </div>
                        {getTrialBadge(org) && (
                          <div className="mt-0.5">{getTrialBadge(org)}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    {getOrgTypeBadge(org)}
                  </td>

                  <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      <span className="font-semibold">{org.active_cameras}</span>
                      <span className="text-gray-500 dark:text-gray-400">/{org.total_cameras}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      de {org.max_cameras} contratadas
                    </div>
                  </td>

                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                      {getMainMetric(org).toLocaleString('pt-BR')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {getMainMetricLabel(org)}
                    </div>
                  </td>

                  <td className="hidden xl:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {org.total_detections_today.toLocaleString('pt-BR')}
                    </div>
                  </td>

                  <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(org.usage_rate, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {org.usage_rate.toFixed(0)}%
                      </span>
                    </div>
                  </td>

                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(org.status)}`}>
                      {getStatusIcon(org.status)} {getStatusLabel(org.status)}
                    </span>
                  </td>

                  <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => toggleSuspectAlerts(org.organization_id, org.suspect_alerts_enabled || false)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                        org.suspect_alerts_enabled
                          ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                      }`}
                      title={org.suspect_alerts_enabled ? 'Clique para desativar' : 'Clique para ativar (+R$ 300/mês)'}
                    >
                      {org.suspect_alerts_enabled ? '✅ Ativo' : '❌ Inativo'}
                    </button>
                  </td>

                  <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => loadOrganizationDetails(org.organization_id)}
                      disabled={loadingDetails && selectedOrg === org.organization_id}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium flex items-center"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">{loadingDetails && selectedOrg === org.organization_id ? 'Carregando...' : 'Ver Detalhes'}</span>
                      <span className="sm:hidden">Ver</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Grid de Alertas e Top Organizações */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organizações com Problemas */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
            Alertas e Atenção
          </h3>
          <div className="space-y-3">
            {organizations
              .filter(org => org.status === 'offline' || org.status === 'error' || org.usage_rate > 90)
              .map(org => (
                <div key={org.organization_id} className="border-l-4 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
                  <p className="font-medium text-gray-900 dark:text-white">{org.organization_name}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {org.status === 'offline' 
                      ? '🔴 Sem detecções nas últimas 2 horas'
                      : org.status === 'error'
                      ? '🟣 Erro ao processar dados'
                      : `⚠️ Utilização alta: ${org.usage_rate.toFixed(0)}%`
                    }
                  </p>
                </div>
              ))}
            {organizations.filter(org => org.status === 'offline' || org.status === 'error' || org.usage_rate > 90).length === 0 && (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                ✅ Todas as organizações operando normalmente
              </p>
            )}
          </div>
        </div>

        {/* Top Organizações */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
            🏆 Top Organizações (Hoje)
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Ordenadas por total de detecções
          </p>
          <div className="space-y-3">
            {[...organizations]
              .sort((a, b) => b.total_detections_today - a.total_detections_today)
              .slice(0, 5)
              .map((org, index) => (
              <div key={org.organization_id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center min-w-0">
                  <span className="text-xl mr-3 flex-shrink-0">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                  </span>
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{org.organization_name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{org.total_detections_today.toLocaleString('pt-BR')} detecções</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-base font-bold text-blue-600 dark:text-blue-400">
                    {getMainMetric(org).toLocaleString('pt-BR')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{getMainMetricLabel(org)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer />
      
      {/* Confirm Modal */}
      <ModalComponent />
    </div>
  );
}

// Componente de Card de Estatística
const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'blue' | 'purple' | 'green' | 'orange';
  subtitle?: string;
}> = ({ title, value, icon, color, subtitle }) => {
  const colors = {
    blue: 'bg-blue-500 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-500 text-purple-600 dark:text-purple-400',
    green: 'bg-green-500 text-green-600 dark:text-green-400',
    orange: 'bg-orange-500 text-orange-600 dark:text-orange-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${colors[color]} bg-opacity-10`}>
          {icon}
        </div>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{title}</p>
      <p className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
  );
};
